import { Check } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { cn } from "@repo/utils";
import { DirectoryList } from "./directory-list";
import { DirectoryNav } from "./directory-nav";
import { DirectoryTrigger } from "./directory-trigger";
import { Button } from "../../button/button";
import { Dialog } from "../../dialog/dialog";
import { DialogContent } from "../../dialog/sections/dialog-content";
import { DialogHeader } from "../../dialog/sections/dialog-header";
import { DialogTitle } from "../../dialog/sections/dialog-title";
import type { Props as InputProps } from "../input";
import type { FsResponse } from "./types";

export type DirectoryInputProps = Omit<InputProps, "onChange" | "value"> & {
  value?: string;
  onChange?: (path: string) => void;
  apiBaseUrl: string;
};

export const DirectoryInput = ({
  className,
  variant,
  inputSize,
  value,
  onChange,
  disabled,
  label,
  error,
  helperText,
  placeholder = "Select a folder...",
  apiBaseUrl,
  ...props
}: DirectoryInputProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<FsResponse | null>(null);

  const fetchDir = useCallback(
    async (path?: string) => {
      setIsLoading(true);
      try {
        const url = path ? `${apiBaseUrl}/fs?path=${encodeURIComponent(path)}` : `${apiBaseUrl}/fs`;

        const res = await fetch(url);
        if (!res.ok) {
          if ((res.status === 400 || res.status === 404) && path) {
            setData({
              currentPath: "",
              parentPath: "",
              folders: [],
              sep: "/",
              error:
                "Directory not found on your machine. Navigate to root" +
                "\n" +
                "(Hint: It is the above Home icon on top left)",
            });
            return;
          }

          throw new Error("Failed to load directory");
        }

        const jsonData = await res.json();
        setData(jsonData);
      } catch (e) {
        console.error("Directory fetch error:", e);
        if (apiBaseUrl?.includes("localhost")) {
          setData({
            currentPath: "",
            parentPath: "",
            folders: [],
            sep: "/",
            error: "Could not connect to GXE Bridge (localhost:3001).",
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [apiBaseUrl]
  );

  useEffect(() => {
    if (isOpen) {
      fetchDir(value || undefined);
    }
  }, [isOpen, fetchDir, value]);

  const handleSelect = () => {
    if (data?.currentPath && onChange) {
      onChange(data.currentPath);
      setIsOpen(false);
    }
  };

  const handleNavigate = (path: string) => {
    fetchDir(path);
  };

  return (
    <div className={cn("w-full", props.containerClassName)}>
      {label && (
        <label className="mb-2 block text-[12px] font-medium">
          {label}
          {props.required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}
      <DirectoryTrigger
        value={value}
        placeholder={placeholder}
        onClick={() => !disabled && setIsOpen(true)}
        disabled={disabled}
        error={error}
        variant={variant}
        inputSize={inputSize}
        className={className}
      />
      {error && <p className="mt-1.5 text-[12px] text-red-500">{error}</p>}
      {!error && helperText && <p className="mt-1.5 text-[12px] text-gray-500">{helperText}</p>}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl gap-0 overflow-hidden p-0">
          <DialogHeader className="border-b bg-gray-50/50 px-4 py-3">
            <DialogTitle>Select Directory</DialogTitle>
          </DialogHeader>

          <div className="flex max-h-[60vh] min-h-[400px] flex-col">
            <DirectoryNav
              currentPath={data?.currentPath}
              parentPath={data?.parentPath}
              isLoading={isLoading}
              onNavigate={handleNavigate}
            />

            <div className="flex flex-1 overflow-y-auto bg-white p-2">
              <DirectoryList
                isLoading={isLoading}
                error={data?.error}
                folders={data?.folders}
                currentPath={data?.currentPath}
                sep={data?.sep}
                onFolderClick={handleNavigate}
              />
            </div>
            <div className="flex justify-end gap-2 border-t bg-gray-50 p-4">
              <Button variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSelect}
                disabled={!data || isLoading || !!data?.error}
                className="gap-2"
              >
                <Check className="h-4 w-4" />
                Select Current Folder
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
